import { formatNumber } from '@angular/common';
import { Injectable } from '@angular/core';
import { DecimalPlaces, DEFAULT_DECIMAL_PLACES, fixedDigitsInfo, toDecimalPlaces } from '@shared/models/decimal-places';
import { MoneyHelper } from '@shared/services/money-helper.service';
import { SystemSettingsService } from './system-settings.service';

/**
 * The organisation's display precision (Settings v3 → Global → Decimal Places).
 *
 * A screen calls `load()` once while it opens — cheap, `SystemSettingsService` caches the
 * Global settings and drops that cache when the settings page saves — and then paints
 * through `current` / `formatAmount` / `formatQuantity`, which are synchronous so pipes and
 * grid cell renderers can use them. Until the first load resolves it holds the defaults.
 */
@Injectable({
  providedIn: 'root'
})
export class DecimalPlacesService {

  private value: DecimalPlaces = DEFAULT_DECIMAL_PLACES;

  constructor(
    private systemSettingsService: SystemSettingsService,
    private moneyHelper: MoneyHelper
  ) { }

  /** Decimals to paint, as last loaded */
  get current(): DecimalPlaces {
    return this.value;
  }

  /** Re-read the setting; the result is also kept in `current` */
  async load(): Promise<DecimalPlaces> {
    const settings = await this.systemSettingsService.GlobalSettings();
    return this.value = {
      amount: toDecimalPlaces('amount', settings.amountsDecimalPlaces),
      quantity: toDecimalPlaces('quantity', settings.quantityDecimalPlaces)
    };
  }

  /** "1,234.50" — what `currencyFormatterV3` paints for an amount at the current width (grids without pipes) */
  formatAmount(value: number): string {
    return this.format(value, this.value.amount);
  }

  /** "10.250" — what `quantityFormatterV3` paints for a quantity at the current width */
  formatQuantity(value: number): string {
    return this.format(value, this.value.quantity);
  }

  private format(value: number, decimals: number): string {
    return formatNumber(this.moneyHelper.round(value, decimals), 'en-US', fixedDigitsInfo(decimals));
  }
}
